import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { arrayMove } from "@dnd-kit/sortable";
import type { DragEndEvent } from "@dnd-kit/core";
import { composerBus } from "@/lib/composerBus";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { useWorkspace } from "@/hooks/useWorkspace";
import {
  deleteUserPrompt,
  duplicateExcerptusPromptToMine,
  fetchPublishedExcerptusPrompts,
  fetchUserPrompts,
  reorderUserPrompts,
  type ExcerptusPrompt,
  type UserPrompt,
} from "@/lib/composerPrompts";
import { applyVariables, parseVariables } from "@/lib/composerPromptVariables";

type RunnablePrompt = {
  id: string;
  title: string;
  body: string;
  modelHint?: string | null;
};

/** State + handlers for PromptsPanel (search, tabs, tag filter, DnD reorder, mutations). */
export const usePromptsPanel = () => {
  const { active } = useWorkspace();
  const { user } = useAuth();
  const { isAdmin } = useUserRole();
  const [excerptus, setExcerptus] = useState<ExcerptusPrompt[]>([]);
  const [mine, setMine] = useState<UserPrompt[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<UserPrompt | null>(null);
  const [creating, setCreating] = useState(false);
  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);
  const [tab, setTab] = useState<"excerptus" | "mine">("mine");
  const [tagFilter, setTagFilter] = useState<string | null>(null);
  const [fillIn, setFillIn] = useState<{ prompt: RunnablePrompt; vars: string[] } | null>(null);

  const isOwner = useMemo(
    () => Boolean(active && user && active.owner_user_id === user.id) || isAdmin,
    [active, user, isAdmin],
  );

  const refresh = async () => {
    if (!active) return;
    setLoading(true);
    try {
      const [ex, me] = await Promise.all([
        fetchPublishedExcerptusPrompts(),
        fetchUserPrompts(active.id),
      ]);
      setExcerptus(ex);
      setMine(me);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active?.id]);

  useEffect(() => {
    setTagFilter(null);
  }, [tab]);

  const handleCopy = async (body: string) => {
    try {
      await navigator.clipboard.writeText(body);
      toast.success("Prompt copied to clipboard");
    } catch {
      toast.error("Could not access clipboard");
    }
  };

  /** Dispatch a finished prompt body to AI Chat with auto-send. */
  const dispatchToChat = (p: RunnablePrompt, finalBody: string) => {
    composerBus.emit("rail:setMode", { mode: "chat" });
    composerBus.emit("chat:insert", {
      text: finalBody,
      source: `prompt:${p.id}`,
      autoSend: true,
      modelHint: p.modelHint ?? null,
    });
    toast.success("Running prompt in Chat", { description: `"${p.title}"` });
  };

  /**
   * "Push & Run" — if the body contains `{placeholder}` tokens, open the
   * fill-in dialog; otherwise dispatch to AI Chat immediately with auto-send.
   */
  const handlePushAndRun = (p: { id: string; body: string; title: string; model_hint?: string | null }) => {
    const runnable: RunnablePrompt = {
      id: p.id,
      title: p.title,
      body: p.body,
      modelHint: p.model_hint ?? null,
    };
    const vars = parseVariables(p.body);
    if (vars.length === 0) {
      dispatchToChat(runnable, p.body);
      return;
    }
    setFillIn({ prompt: runnable, vars });
  };

  const handleFillInRun = (values: Record<string, string>) => {
    if (!fillIn) return;
    const finalBody = applyVariables(fillIn.prompt.body, values);
    dispatchToChat(fillIn.prompt, finalBody);
    setFillIn(null);
  };

  const handleDuplicate = async (p: ExcerptusPrompt) => {
    if (!active) return;
    try {
      const created = await duplicateExcerptusPromptToMine(p, active.id);
      setMine((cur) => [...cur, created]);
      toast.success(`Saved "${created.title}" to My Prompts`);
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDelete = async (p: UserPrompt) => {
    if (!confirm(`Delete prompt "${p.title}"?`)) return;
    try {
      await deleteUserPrompt(p.id);
      toast.success("Prompt deleted");
      void refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    if (!active) return;
    const { active: a, over } = e;
    if (!over || a.id === over.id) return;
    const oldIndex = mine.findIndex((p) => p.id === a.id);
    const newIndex = mine.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(mine, oldIndex, newIndex);
    setMine(next);
    try {
      await reorderUserPrompts(active.id, next.map((p) => p.id));
    } catch (err) {
      toast.error((err as Error).message ?? "Could not save order");
      void refresh();
    }
  };

  const tabTags = useMemo(() => {
    const set = new Set<string>();
    const list = tab === "excerptus" ? excerptus : mine;
    for (const p of list) for (const t of p.tags ?? []) set.add(t);
    return Array.from(set).sort();
  }, [tab, excerptus, mine]);

  const filterFn = <T extends { title: string; description?: string | null; tags: string[] }>(
    list: T[],
  ): T[] => {
    const q = search.trim().toLowerCase();
    return list.filter((p) => {
      if (tagFilter && !(p.tags ?? []).includes(tagFilter)) return false;
      if (!q) return true;
      return (
        p.title.toLowerCase().includes(q) ||
        (p.description ?? "").toLowerCase().includes(q) ||
        (p.tags ?? []).some((t) => t.toLowerCase().includes(q))
      );
    });
  };

  return {
    active, isOwner,
    excerptus, mine, loading,
    search, setSearch,
    editing, setEditing,
    creating, setCreating,
    tagsManagerOpen, setTagsManagerOpen,
    tab, setTab,
    tagFilter, setTagFilter,
    tabTags,
    filteredExcerptus: filterFn(excerptus),
    filteredMine: filterFn(mine),
    refresh,
    setMine,
    fillIn, setFillIn,
    handleCopy, handlePushAndRun, handleFillInRun, handleDuplicate, handleDelete, handleDragEnd,
  };
};
